import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import { useMapEngine } from '../map-engine/MapEngineContext';
import type { LayerId } from '../../types/mapEngine';
import type { ConopsFeature } from '../../types/conops';

const CONOPS_SOURCE = 'conops-features';
const CUSTOM_TWY_SOURCE = 'custom-taxiways';

// LayerId → GL layer IDs controlled by it
const CONOPS_VIS_MAP: [LayerId, string[]][] = [
  ['conops-aor',               ['aor-fill', 'aor-border']],
  ['conops-taxi-routes',       ['taxi-routes']],
  ['conops-pushback-profiles', ['pushback-lines']],
  ['conops-aptr',              ['aptr-blue', 'aptr-orange', 'aptr-labels']],
  ['conops-roundabout',        ['roundabout-fill', 'roundabout-line', 'roundabout-label']],
];

function initConopsLayers(map: mapboxgl.Map): void {
  if (map.getSource(CONOPS_SOURCE)) return;

  map.addSource(CONOPS_SOURCE, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });

  map.addLayer({ id: 'aor-fill', type: 'fill', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'ams_zone'],
    paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.2 } });
  map.addLayer({ id: 'aor-border', type: 'line', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'ams_zone'],
    paint: { 'line-color': ['get', 'color'], 'line-width': 2 } });
  map.addLayer({ id: 'taxi-routes', type: 'line', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'taxi_route'],
    paint: { 'line-color': '#F26430', 'line-width': 3, 'line-dasharray': [2, 1] } });
  map.addLayer({ id: 'pushback-lines', type: 'line', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'pushback_profile'],
    paint: { 'line-color': '#9B59B6', 'line-width': 2 } });
  map.addLayer({ id: 'aptr-blue', type: 'line', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'aptr_blue'],
    paint: { 'line-color': '#1E88E5', 'line-width': 3.5 } });
  map.addLayer({ id: 'aptr-orange', type: 'line', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'aptr_orange'],
    paint: { 'line-color': '#F26430', 'line-width': 3.5 } });
  map.addLayer({ id: 'aptr-labels', type: 'symbol', source: CONOPS_SOURCE,
    filter: ['in', ['get', 'feature_type'], ['literal', ['aptr_blue', 'aptr_orange']]],
    layout: {
      'symbol-placement': 'line',
      'text-field': ['get', 'name'],
      'text-size': 10,
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Regular'],
      'text-offset': [0, 0.8],
    } as any,
    paint: {
      'text-color': ['match', ['get', 'feature_type'], 'aptr_blue', '#90CAF9', '#FFCC80'],
      'text-halo-color': '#0d0d14',
      'text-halo-width': 1.2,
    },
  });
  map.addLayer({ id: 'roundabout-fill', type: 'fill', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'roundabout'],
    paint: { 'fill-color': '#FFD700', 'fill-opacity': 0.06 } });
  map.addLayer({ id: 'roundabout-line', type: 'line', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'roundabout'],
    paint: { 'line-color': '#FFD700', 'line-width': 2.5, 'line-dasharray': [4, 2] } });
  map.addLayer({ id: 'roundabout-label', type: 'symbol', source: CONOPS_SOURCE,
    filter: ['==', ['get', 'feature_type'], 'roundabout'],
    layout: {
      'text-field': ['get', 'name'],
      'text-size': 11,
      'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
    } as any,
    paint: {
      'text-color': '#FFD700',
      'text-halo-color': '#0d0d14',
      'text-halo-width': 1.5,
    },
  });
}

function initCustomTwyLayer(map: mapboxgl.Map): void {
  if (map.getSource(CUSTOM_TWY_SOURCE)) return;
  map.addSource(CUSTOM_TWY_SOURCE, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });
  map.addLayer({
    id: 'custom-taxiways-line',
    type: 'line',
    source: CUSTOM_TWY_SOURCE,
    paint: { 'line-color': '#FFD700', 'line-width': 1.8 },
  });
}

interface ConopsPluginProps {
  features: ConopsFeature[];
  customTaxiwaysFC: GeoJSON.FeatureCollection | null;
  onDrawReady: (draw: InstanceType<typeof MapboxDraw>) => void;
  onDrawCreate: (geom: GeoJSON.Geometry, drawId: string) => void;
}

export default function ConopsPlugin({
  features,
  customTaxiwaysFC,
  onDrawReady,
  onDrawCreate,
}: ConopsPluginProps) {
  const { map, mapLoaded, layerVisibility } = useMapEngine();
  const onDrawCreateRef = useRef(onDrawCreate);
  onDrawCreateRef.current = onDrawCreate;

  // Init CONOPS source + layers + MapboxDraw
  useEffect(() => {
    if (!map || !mapLoaded) return;
    initConopsLayers(map);
    initCustomTwyLayer(map);

    const draw = new MapboxDraw({ displayControlsDefault: false, controls: {} });
    map.addControl(draw as any);
    onDrawReady(draw);

    const handleCreate = (e: { features: GeoJSON.Feature[] }) => {
      if (!e.features.length) return;
      onDrawCreateRef.current(e.features[0].geometry, e.features[0].id as string);
    };
    map.on('draw.create', handleCreate);

    return () => {
      map.off('draw.create', handleCreate);
      try { map.removeControl(draw as any); } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapLoaded]);

  // Sync features → CONOPS source
  useEffect(() => {
    if (!map || !mapLoaded) return;
    const src = map.getSource(CONOPS_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    src?.setData({ type: 'FeatureCollection', features });
  }, [features, map, mapLoaded]);

  // Sync custom taxiways → source
  useEffect(() => {
    if (!map || !mapLoaded) return;
    const src = map.getSource(CUSTOM_TWY_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    src?.setData(customTaxiwaysFC ?? { type: 'FeatureCollection', features: [] });
  }, [customTaxiwaysFC, map, mapLoaded]);

  // Apply CONOPS layer visibility
  useEffect(() => {
    if (!map || !mapLoaded) return;
    for (const [layerId, glIds] of CONOPS_VIS_MAP) {
      const vis = (layerVisibility[layerId] ?? true) ? 'visible' : 'none';
      for (const glId of glIds) {
        if (map.getLayer(glId)) map.setLayoutProperty(glId, 'visibility', vis);
      }
    }
    // custom-taxiways-line follows base-taxiways visibility
    if (map.getLayer('custom-taxiways-line')) {
      const twyVis = (layerVisibility['base-taxiways'] ?? true) ? 'visible' : 'none';
      map.setLayoutProperty('custom-taxiways-line', 'visibility', twyVis);
    }
  }, [layerVisibility, map, mapLoaded]);

  return null;
}
